'use client';

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createGame, tick, typeChar, currentSettings, urgency } from '@/features/games/falling-words/logic';
import { recordGameAttempt, getGameRecord } from '@/lib/storage/games';
import { appendAttempt } from '@/lib/storage/attempts';
import { useGameLoop } from '@/hooks/use-game-loop';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export function FallingWordsGame({ defaultDifficulty = 'medium' }) {
  const [difficulty, setDifficulty] = useState(defaultDifficulty);
  const [seed, setSeed] = useState(1);
  const [state, setState] = useState(() => createGame({ seed: 1, difficulty: defaultDifficulty }));
  const [now, setNow] = useState(0);
  const [motionOk, setMotionOk] = useState(true);
  const [record, setRecord] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setMotionOk(!mq.matches);
    const listener = () => setMotionOk(!mq.matches);
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
  }, []);

  useEffect(() => {
    setRecord(getGameRecord(`falling-words:${difficulty}`));
  }, [difficulty]);

  useGameLoop({
    phase: state.phase,
    tick: (nowMs) => {
      setNow(nowMs);
      setState((s) => (s.phase === 'running' ? tick(s, nowMs) : s));
    },
  });

  useEffect(() => {
    if (state.phase === 'ended' && state.startedAtMs > 0) {
      const elapsedSec = Math.max(1, (now - state.startedAtMs) / 1000);
      const wpm = (state.correctTyped * 5 * 60) / elapsedSec;
      const updated = recordGameAttempt({
        gameId: `falling-words:${difficulty}`,
        score: state.score,
        wpm,
      });
      setRecord(updated);
      appendAttempt({
        activityType: 'game',
        activityRefSlug: `falling-words:${difficulty}`,
        durationSeconds: Math.round(elapsedSec),
        grossWpm: 0,
        netWpm: Math.round(wpm),
        accuracy: state.correctTyped + state.incorrectTyped > 0
          ? (state.correctTyped / (state.correctTyped + state.incorrectTyped)) * 100
          : 100,
        correctChars: state.correctTyped * 4,
        incorrectChars: state.incorrectTyped,
        totalChars: state.correctTyped * 4 + state.incorrectTyped,
        backspaces: 0,
      });
    }
  }, [state.phase, state.score, state.correctTyped, state.incorrectTyped, state.startedAtMs, difficulty, now]);

  useEffect(() => {
    if (state.phase === 'running') inputRef.current?.focus();
  }, [state.phase]);

  const reset = useCallback((nextDifficulty = difficulty) => {
    const nextSeed = seed + 1;
    setSeed(nextSeed);
    setState(createGame({ seed: nextSeed, difficulty: nextDifficulty }));
    setNow(0);
  }, [seed, difficulty]);

  const onKeyDown = (e) => {
    if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
    e.preventDefault();
    setState((s) => typeChar(s, e.key));
  };

  const settings = currentSettings(state);
  const urg = urgency(state, now || state.startedAtMs || 1);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1">
          {['easy', 'medium', 'hard'].map((d) => (
            <Button
              key={d}
              variant={difficulty === d ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => { setDifficulty(d); reset(d); }}
              disabled={state.phase === 'running'}
            >
              {d}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-4 text-sm tabular-nums">
          <span className="text-text-secondary">Score <strong className="text-text-primary">{state.score}</strong></span>
          <span className="text-text-secondary">Lives <strong className="text-text-primary">{state.lives}</strong></span>
          <span className="text-text-secondary">Combo <strong className="text-text-primary">{state.combo}</strong></span>
        </div>
      </div>

      {motionOk ? (
        <div className="relative h-80 overflow-hidden rounded-lg border border-border bg-surface-sunken">
          {state.words.map((w) => {
            const p = Math.min(1, (now - w.spawnedAtMs) / w.fallDurationMs);
            return (
              <div
                key={w.id}
                className="absolute rounded bg-brand-100 px-2 py-1 font-mono text-sm text-brand-900 shadow-sm dark:bg-brand-900 dark:text-brand-100"
                style={{
                  top: `${p * 280}px`,
                  left: `${(w.id.charCodeAt(2) * 17) % 70}%`,
                }}
              >
                {state.typedBuffer && w.text.toLowerCase().startsWith(state.typedBuffer.toLowerCase()) ? (
                  <>
                    <span className="bg-brand-300">{w.text.slice(0, state.typedBuffer.length)}</span>
                    {w.text.slice(state.typedBuffer.length)}
                  </>
                ) : w.text}
              </div>
            );
          })}
        </div>
      ) : (
        <Card title="Falling Words (reduced motion)">
          <p className="mb-2 text-sm text-text-muted">Progress shown as height instead of animation.</p>
          <ul className="space-y-2">
            {state.words.map((w) => {
              const p = Math.min(1, (now - w.spawnedAtMs) / w.fallDurationMs);
              return (
                <li key={w.id} className="font-mono text-sm">
                  {w.text}{' '}
                  <span className="text-text-muted">— {(1 - p).toFixed(1)} remaining</span>
                  <div className="mt-1 h-1 w-full rounded bg-surface-sunken">
                    <div className="h-full rounded bg-brand-500" style={{ width: `${p * 100}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <div aria-live="polite" className="sr-only">
        {state.words.length} words · lives {state.lives} · score {state.score}
      </div>

      <input
        ref={inputRef}
        className="sr-only"
        aria-label="Falling Words input"
        value=""
        readOnly
        onKeyDown={onKeyDown}
        tabIndex={state.phase === 'running' ? 0 : -1}
      />

      {state.phase === 'ended' && (
        <Card title="Game over">
          <p className="text-sm text-text-secondary">
            Score: <strong className="tabular-nums text-text-primary">{state.score}</strong>
            {record && <> · Best: <strong className="tabular-nums">{record.bestScore}</strong></>}
          </p>
          <div className="mt-4 flex gap-2">
            <Button onClick={() => reset()}>Play again</Button>
          </div>
        </Card>
      )}

      {state.phase === 'running' && state.words.length === 0 && (
        <p className="text-center text-sm text-text-muted">
          Type the words as they fall. Press any letter to begin.
        </p>
      )}
    </div>
  );
}